import { Icon } from '@web/components/ui/icons'
import { appIcon } from '@web/lib/appIcon'
import type { MessageActor } from 'cc-render'
import { createContext, type ReactNode, useContext, useState } from 'react'

const MessageActors = createContext<{
  user?: MessageActor
  app?: MessageActor
}>({})

export function MessageActorsProvider({
  user,
  app,
  children,
}: {
  user?: MessageActor
  app?: MessageActor
  children: ReactNode
}) {
  return (
    <MessageActors.Provider value={{ user, app }}>
      {children}
    </MessageActors.Provider>
  )
}

/** Resolve unattributed messages against their session, never against the active viewer. */
export function useMessageActor(
  role: 'user' | 'assistant',
  actor?: MessageActor,
): MessageActor {
  const defaults = useContext(MessageActors)
  if (actor) {
    const known = [defaults.user, defaults.app].find(
      (item) => item?.kind === actor.kind && item.id === actor.id,
    )
    return {
      ...known,
      ...actor,
      name:
        actor.name ||
        known?.name ||
        (actor.kind === 'agent' ? 'Agent' : actor.id),
      avatarUrl:
        actor.avatarUrl ||
        known?.avatarUrl ||
        (actor.kind === 'agent' ? defaults.app?.avatarUrl : undefined),
    }
  }
  return (
    (role === 'user' ? defaults.user : defaults.app) ?? {
      kind: role === 'user' ? 'user' : 'agent',
      id: role,
      name: role === 'user' ? 'User' : 'Agent',
    }
  )
}

export function MessageAvatar({
  actor,
  size = 32,
}: {
  actor: MessageActor
  size?: number
}) {
  const [failedUrl, setFailedUrl] = useState<string>()
  const label = actor.name || actor.id
  const photo = actor.kind === 'user'
  const avatarUrl =
    !photo && actor.avatarUrl?.startsWith('/v1/apps/')
      ? appIcon(actor.avatarUrl, 64)
      : actor.avatarUrl
  const small = size <= 20
  return (
    <span
      className={`message-avatar ${small ? '' : 'mt-1'} grid shrink-0 place-items-center overflow-hidden border border-line bg-panel text-muted ${small ? 'text-[9px]' : 'text-xs'} ${photo ? 'rounded-full' : 'rounded-lg'}`}
      style={{ width: size, height: size }}
      data-actor-kind={actor.kind}
      data-actor-id={actor.id}
      role="img"
      aria-label={label}
      title={label}
    >
      {avatarUrl && failedUrl !== avatarUrl ? (
        <img
          className={`h-full w-full ${photo ? 'object-cover' : 'object-contain'}`}
          src={avatarUrl}
          width={size}
          height={size}
          decoding="async"
          alt=""
          onError={() => setFailedUrl(avatarUrl)}
        />
      ) : actor.name ? (
        <span aria-hidden="true">
          {label
            .split(/\s+/)
            .slice(0, 2)
            .map((part) => part[0])
            .join('')}
        </span>
      ) : (
        <Icon name="gauge" size={small ? 12 : 16} />
      )}
    </span>
  )
}
